import { isPrerenderUserAgent } from '../util/PrerenderUtil';
import React, { createContext, useContext, useEffect, useRef, useState, useMemo, useCallback, ReactNode } from 'react';
import {
  initializeFaro,
  getWebInstrumentations,
  Faro,
  LogLevel,
} from '@grafana/faro-web-sdk';
import { useFeatureFlags } from '../hooks/routing/useFeatureFlags';

/** Faro collector endpoint - routes through ALB to Grafana Alloy */
const FARO_COLLECTOR_URL = 'https://grafana.secur.space/collect';

/** Application name for Faro telemetry */
const APP_NAME = 'securspace-ui';

/**
 * User interface matching the shape from pendo-context
 */
export interface FaroUser {
  id: string;
  email?: string;
  loggedInUsername?: string;
  companyName?: string;
  type?: string;      // Account type: SUPPLIER, BUYER, ADMIN
  userType?: string;  // User role within account: ADMIN, OWNER, MANAGER, etc.
  [key: string]: any;
}

interface FaroContextValue {
  initialized: boolean;
  currentUser: FaroUser | null;
  /** Manually push an error to Faro */
  pushError: (error: Error, context?: Record<string, string>) => void;
  /** Manually push a log message to Faro */
  pushLog: (message: string, level?: LogLevel, context?: Record<string, string>) => void;
  /** Push a custom event to Faro */
  pushEvent: (name: string, attributes?: Record<string, string>, domain?: string) => void;
}

interface FaroProviderProps {
  children: ReactNode;
  user: FaroUser | null;
}

const FaroContext = createContext<FaroContextValue | undefined>(undefined);

/**
 * Determines the environment based on the current hostname.
 * Returns null for localhost to disable telemetry in local development.
 */
function getEnvironment(): string | null {
  const hostname = window.location.hostname;

  // Disable Faro on localhost - no telemetry in local development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return null;
  }

  // Environment detection based on hostname
  if (hostname === 'test.secur.space') {
    return 'test';
  }
  if (hostname === 'sandbox.secur.space') {
    return 'sandbox';
  }
  if (hostname === 'secur.space' || hostname === 'www.secur.space') {
    return 'production';
  }

  // Default for any other environment (e.g., PR previews, staging)
  return null;
}

/**
 * Hook to access Faro context.
 * Must be used within a FaroProvider.
 */
export const useFaro = (): FaroContextValue => {
  const context = useContext(FaroContext);
  if (!context) {
    throw new Error('useFaro must be used within a FaroProvider');
  }
  return context;
};

/**
 * FaroProvider - Manages Grafana Faro SDK lifecycle and user context.
 *
 * Follows the same pattern as PendoProvider:
 * - Initializes Faro once on mount (if enabled via LaunchDarkly)
 * - Automatically syncs user context when user prop changes
 * - Clears user context on logout (when user becomes null)
 *
 * LaunchDarkly Flag: `faroTelemetry`
 * - true: Faro telemetry is enabled for this environment
 * - false: Faro telemetry is disabled (no data sent)
 *
 * @example
 * <FaroProvider user={this.state.user}>
 *   <App />
 * </FaroProvider>
 */
export const FaroProvider: React.FC<FaroProviderProps> = ({ children, user }) => {
  const faroRef = useRef<Faro | null>(null);
  const [initialized, setInitialized] = useState(false);
  const [currentUser, setCurrentUser] = useState<FaroUser | null>(null);

  // LaunchDarkly flag to control Faro telemetry
  const { faroTelemetry } = useFeatureFlags();

  // Initialize Faro when the component mounts (app starts)
  useEffect(() => {
    if (faroRef.current) {
      return; // Already initialized
    }

    // Check LaunchDarkly flag first; and never send telemetry from a
    // prerender snapshot (securspace-aws#20) — it is a bot render, not a user.
    if (!faroTelemetry || isPrerenderUserAgent()) {
      console.debug('[Faro] Disabled via LaunchDarkly flag - telemetry will not be sent');
      setInitialized(true); // Mark as "initialized" so context is available
      return;
    }

    const environment = getEnvironment();

    // Skip initialization on localhost
    if (environment === null) {
      console.debug('[Faro] Disabled on localhost - telemetry will not be sent');
      setInitialized(true); // Mark as "initialized" so context is available
      return;
    }

    try {
      faroRef.current = initializeFaro({
        url: FARO_COLLECTOR_URL,
        app: {
          name: APP_NAME,
          version: import.meta.env.VITE_APP_VERSION || 'unknown',
          environment: environment,
        },
        beforeSend: (event) => {
          try {
            // Suppress Google Maps Vector Map fallback warning (non-critical, map still renders via raster)
            // Check specific payload fields instead of serializing the entire transport item
            const payload = event?.payload as Record<string, unknown> | undefined;
            const message = (payload?.message ?? payload?.value ?? '') as string;
            if (typeof message === 'string' && message.includes('Attempted to load a Vector Map')) {
              return null;
            }
          } catch {
            // If inspection fails, return the event to avoid breaking telemetry
          }
          return event;
        },
        instrumentations: [
          ...getWebInstrumentations({
            captureConsole: true,
          }),
        ],
      });

      console.debug(`[Faro] Initialized for environment: ${environment}`);
      setInitialized(true);
    } catch (error) {
      console.error('[Faro] Failed to initialize:', error);
      setInitialized(true); // Still mark as initialized to not block app
    }
  }, [faroTelemetry]);

  // Sync user context with Faro when user changes
  useEffect(() => {
    if (user && user.id) {
      // User logged in or data updated
      if (faroRef.current) {
        try {
          // Sanitize companyName for logfmt compatibility
          // Replace spaces with underscores to ensure proper field extraction in Loki
          const sanitizedCompanyName = (user.companyName || '').replace(/\s+/g, '_');

          faroRef.current.api.setUser({
            id: user.id,
            email: user.email,
            username: user.loggedInUsername,
            attributes: {
              companyName: sanitizedCompanyName,
              accountType: user.type || '',      // SUPPLIER, BUYER, ADMIN
              userRole: user.userType || '',     // ADMIN, OWNER, MANAGER, etc.
            },
          });
          console.debug('[Faro] User context updated');
        } catch (error) {
          console.error('[Faro] Failed to update user info:', error);
        }
      }
      setCurrentUser(user);
    } else if (!user && currentUser) {
      // User logged out
      if (faroRef.current) {
        try {
          faroRef.current.api.resetUser();
          console.debug('[Faro] User context cleared');
        } catch (error) {
          console.error('[Faro] Failed to clear user info:', error);
        }
      }
      setCurrentUser(null);
    }
  }, [user, currentUser]);

  /**
   * Manually pushes an error to Faro.
   * Use this for errors that might not be automatically captured.
   */
  const pushError = useCallback((error: Error, context?: Record<string, string>): void => {
    if (!faroRef.current) {
      console.error('[Faro] Cannot push error - not initialized:', error);
      return;
    }

    try {
      faroRef.current.api.pushError(error, { context });
    } catch (e) {
      console.error('[Faro] Failed to push error:', e);
    }
  }, []);

  /**
   * Manually pushes a log message to Faro.
   */
  const pushLog = useCallback((
    message: string,
    level: LogLevel = LogLevel.INFO,
    context?: Record<string, string>
  ): void => {
    if (!faroRef.current) {
      return;
    }

    try {
      faroRef.current.api.pushLog([message], { level, context });
    } catch (error) {
      console.error('[Faro] Failed to push log:', error);
    }
  }, []);

  /**
   * Pushes a custom event to Faro.
   * Use this to track business events or user interactions.
   */
  const pushEvent = useCallback((
    name: string,
    attributes?: Record<string, string>,
    domain?: string
  ): void => {
    if (!faroRef.current) {
      return;
    }

    try {
      faroRef.current.api.pushEvent(name, attributes, domain);
    } catch (error) {
      console.error('[Faro] Failed to push event:', error);
    }
  }, []);

  const contextValue = useMemo(() => ({
    initialized,
    currentUser,
    pushError,
    pushLog,
    pushEvent,
  }), [initialized, currentUser, pushError, pushLog, pushEvent]);

  return (
    <FaroContext.Provider value={contextValue}>
      {children}
    </FaroContext.Provider>
  );
};
